import {
  TEXT_FORMAT_TRANSFORMERS,
  UNORDERED_LIST,
  ORDERED_LIST,
  QUOTE,
  type Transformer,
} from "@lexical/markdown";

/**
 * Shared markdown transformers for AI Planner rich text fields (SKE-2727)
 *
 * Used by:
 * - SkedEditor's MarkdownShortcutPlugin (typing shortcuts)
 * - ShareTextContext.setText() (AI-generated content parsing)
 *
 * Supports:
 * - **bold** and __bold__
 * - *italic* and _italic_
 * - ~~strikethrough~~
 * - `inline code`
 * - Unordered lists (- item)
 * - Ordered lists (1. item)
 * - Blockquotes (> quote)
 */
export const IDEA_PLANNER_MARKDOWN_TRANSFORMERS: Transformer[] = [
  ...TEXT_FORMAT_TRANSFORMERS,
  UNORDERED_LIST,
  ORDERED_LIST,
  QUOTE,
];

/**
 * Detects if text contains markdown syntax that should be parsed
 *
 * Patterns detected:
 * - **bold** or __bold__
 * - *italic* or _italic_ (single asterisk/underscore)
 * - ~~strikethrough~~
 * - `inline code`
 * - Lists at start of line: "- item" or "1. item"
 * - Blockquotes at start of line: "> quote"
 *
 * Uses multiline flag (m) to match ^ at start of any line
 * Uses Safari-compatible regex patterns (no lookbehind assertions)
 */
export function hasMarkdownSyntax(text: string): boolean {
  // Early exit for short text or text without any markdown characters
  if (text.length < 3 || !/[*_`~>\d-]/.test(text)) {
    return false;
  }

  // Bold: **text** or __text__
  if (/\*\*[^*]+\*\*/.test(text)) return true;
  if (/__[^_]+__/.test(text)) return true;

  // Italic: *text* (single asterisk, not bold **)
  // Safari-compatible: match *word* pattern where word has no spaces at boundaries
  // and text doesn't contain ** (which would indicate bold)
  if (/\*[^*\s][^*]*\*/.test(text) && !/\*\*/.test(text)) return true;

  // Italic: _text_ (single underscore, not bold __)
  if (/_[^_\s][^_]*_/.test(text) && !/__/.test(text)) return true;

  // Strikethrough: ~~text~~
  if (/~~[^~]+~~/.test(text)) return true;

  // Inline code: `text`
  if (/`[^`]+`/.test(text)) return true;

  // Unordered list at start of line
  if (/^- /m.test(text)) return true;

  // Ordered list at start of line
  if (/^\d+\. /m.test(text)) return true;

  // Blockquote at start of line
  if (/^> /m.test(text)) return true;

  return false;
}
